import * as React from "react"
import { cn } from "../../lib/utils"

export interface TimeInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {}

const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(
  ({ className, placeholder = "Select time", onClick, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    
    // Combine refs
    React.useImperativeHandle(ref, () => inputRef.current!);
    
    const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
      // Open picker on any click within the input
      if (inputRef.current) {
        inputRef.current.showPicker?.();
      }
      onClick?.(e);
    };
    
    const handleIconClick = () => {
      if (inputRef.current) {
        inputRef.current.showPicker?.();
        inputRef.current.focus();
      }
    };
    
    return (
      <div className={cn("relative", className)}>
        <button
          type="button"
          onClick={handleIconClick}
          className="absolute inset-y-0 left-0 pl-4 flex items-center z-10 cursor-pointer hover:opacity-80"
          tabIndex={-1}
        >
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
        <input
          type="time"
          ref={inputRef}
          placeholder={placeholder}
          onClick={handleClick}
          className={cn(
            "h-12 w-full rounded-lg border border-gray-300 bg-white pl-12 pr-4 py-2 text-sm text-gray-900 transition-all",
            "hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-aalaw-red focus:border-transparent",
            "cursor-pointer"
          )}
          style={{
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none',
          }}
          {...props}
        />
        <style>{`
          input[type="time"]::-webkit-datetime-edit {
            width: 100%;
          }
          input[type="time"]::-webkit-datetime-edit-fields-wrapper {
            width: 100%;
            justify-content: flex-start;
          }
          input[type="time"]::-webkit-calendar-picker-indicator {
            position: absolute;
            right: 12px;
            cursor: pointer;
            opacity: 0.6;
          }
          input[type="time"]::-webkit-calendar-picker-indicator:hover {
            opacity: 1;
          }
        `}</style>
      </div>
    )
  }
)
TimeInput.displayName = "TimeInput"

export { TimeInput }
